import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { fabric } from 'fabric';
import { ChromePicker } from 'react-color';
import api, { unwrap } from '../utils/api';
import { addBrandingLogoToCanvas, readBrandingLogoPosition } from '../utils/brandingLogo';
import { addOverlayToCanvas, readOverlayPosition, removeOverlayFromCanvas } from '../utils/overlayImage';
import { swapHebrewQuotes } from '../utils/hebrewQuotes';
import Icon, { withCheck } from '../components/Icon';

// Snap behaviour: while the user drags a textbox, check if any of its edges /
// center align to within SNAP_PX of another textbox's edge / center, or the
// canvas's own center line. If so, nudge the textbox into exact alignment and
// remember the guide line to draw on the overlay canvas.
const SNAP_PX = 8;

function snapOnMove(canvas, e) {
  const target = e.target;
  if (!target || target._isBrandingLogo || target._isOverlayImage) return;
  const natW = canvas._naturalW || canvas.getWidth();
  const natH = canvas._naturalH || canvas.getHeight();
  const tw = target.getScaledWidth();
  const th = target.getScaledHeight();

  const candidates = canvas.getObjects().filter(o => o !== target && o.type === 'textbox');

  const vLines = [natW / 2]; // canvas center
  const hLines = [natH / 2];
  for (const o of candidates) {
    const w = o.getScaledWidth();
    const h = o.getScaledHeight();
    vLines.push(o.left, o.left + w / 2, o.left + w);
    hLines.push(o.top, o.top + h / 2, o.top + h);
  }

  const guides = [];
  let bestDx = null; let bestDxDist = SNAP_PX + 1; let bestVLine = null;
  for (const line of vLines) {
    for (const [offset, srcLabel] of [[0, 'left'], [tw / 2, 'center'], [tw, 'right']]) {
      const src = target.left + offset;
      const d = line - src;
      if (Math.abs(d) < bestDxDist) { bestDxDist = Math.abs(d); bestDx = d; bestVLine = line; }
    }
  }
  let bestDy = null; let bestDyDist = SNAP_PX + 1; let bestHLine = null;
  for (const line of hLines) {
    for (const [offset] of [[0], [th / 2], [th]]) {
      const src = target.top + offset;
      const d = line - src;
      if (Math.abs(d) < bestDyDist) { bestDyDist = Math.abs(d); bestDy = d; bestHLine = line; }
    }
  }

  if (bestDx !== null && bestDxDist <= SNAP_PX) {
    target.set({ left: target.left + bestDx });
    guides.push({ type: 'v', pos: bestVLine });
  }
  if (bestDy !== null && bestDyDist <= SNAP_PX) {
    target.set({ top: target.top + bestDy });
    guides.push({ type: 'h', pos: bestHLine });
  }
  target.setCoords();
  drawGuides(canvas, guides);
}

function drawGuides(canvas, guides) {
  const ctx = canvas.getSelectionContext();
  const w = canvas.getWidth();
  const h = canvas.getHeight();
  const zoom = canvas.getZoom();
  ctx.save();
  ctx.clearRect(0, 0, w, h);
  ctx.strokeStyle = '#29B4F1';
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  for (const g of guides) {
    ctx.beginPath();
    if (g.type === 'v') {
      const x = g.pos * zoom;
      ctx.moveTo(x, 0); ctx.lineTo(x, h);
    } else {
      const y = g.pos * zoom;
      ctx.moveTo(0, y); ctx.lineTo(w, y);
    }
    ctx.stroke();
  }
  ctx.restore();
}

function clearGuides(canvas) {
  const ctx = canvas.getSelectionContext();
  ctx.clearRect(0, 0, canvas.getWidth(), canvas.getHeight());
}

// Font loader — registers a @font-face via the FontFace API so the canvas
// can actually render that typeface.
const loadedFonts = new Set();
async function ensureFont(name, url, weight = '400') {
  if (!name || !url) return;
  const key = `${name}-${weight}`;
  if (loadedFonts.has(key)) return;
  try {
    const face = new FontFace(name, `url(${url})`, { weight: String(weight) });
    await face.load();
    document.fonts.add(face);
    loadedFonts.add(key);
  } catch (e) {
    console.warn('Font load failed:', name, e.message);
  }
}

const WRAPPER_KEYS = [
  'autocompleteLabel', 'autofillableField',
  'originalText',
  'multiLine', 'isUpperCase', 'direction',
  'horizontallyCentered', 'maxFontSize', 'maxWidth',
  'editable', 'order', 'prefilled',
];

const FABRIC_KEYS = [
  'type', 'version', 'originX', 'originY', 'left', 'top', 'width', 'height',
  'fill', 'stroke', 'strokeWidth', 'strokeDashArray', 'strokeLineCap',
  'strokeDashOffset', 'strokeLineJoin', 'strokeUniform', 'strokeMiterLimit',
  'scaleX', 'scaleY', 'angle', 'flipX', 'flipY', 'opacity', 'shadow',
  'visible', 'backgroundColor', 'fillRule', 'paintFirst', 'globalCompositeOperation',
  'skewX', 'skewY', 'text', 'fontSize', 'fontWeight', 'fontFamily', 'fontStyle',
  'lineHeight', 'underline', 'overline', 'linethrough', 'textAlign',
  'textBackgroundColor', 'charSpacing', 'minWidth', 'splitByGrapheme', 'styles',
];

export default function EditProductFields() {
  const { id } = useParams();
  const stageRef = useRef(null);
  const canvasElRef = useRef(null);
  const fabricRef = useRef(null);
  const [product, setProduct] = useState(null);
  const [fields, setFields] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [fontSearch, setFontSearch] = useState('');
  const [autofillSlots, setAutofillSlots] = useState([]);
  const [peopleNames, setPeopleNames] = useState([]);
  const [shuls, setShuls] = useState([]);
  const [halls, setHalls] = useState([]);
  const [layoutPresets, setLayoutPresets] = useState([]);
  const [presetMsg, setPresetMsg] = useState('');
  const [selected, setSelected] = useState(null);
  const [draftMeta, setDraftMeta] = useState({});
  const [draftText, setDraftText] = useState('');
  const [showColor, setShowColor] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateMsg, setGenerateMsg] = useState('');
  const [savingAll, setSavingAll] = useState(false);
  const [saveAllMsg, setSaveAllMsg] = useState('');
  const [clipboardStyle, setClipboardStyle] = useState(null);
  const [, forceRerender] = useState(0);
  const [brandingImageUrl, setBrandingImageUrl] = useState('');
  const [brandingColor, setBrandingColor] = useState('#FFFFFF');
  const [showBrandingColor, setShowBrandingColor] = useState(false);
  // Overlay images — multiple supported. Each entry has a synthetic _overlayId
  // used to match its fabric.Image on the canvas; the id is admin-session
  // local and doesn't need to be persisted.
  const [overlays, setOverlays] = useState([]);
  const [overlayBusy, setOverlayBusy] = useState(false);
  const [showOverlayColor, setShowOverlayColor] = useState(false);
  // Which overlay's controls are currently shown in the side panel.
  const [activeOverlayId, setActiveOverlayId] = useState(null);
  // 'logo' | 'overlay' | null — when set, the side panel shows controls for
  // that special object instead of the text-field controls.
  const [selectedSpecial, setSelectedSpecial] = useState(null);
  const [watermarks, setWatermarks] = useState([]);
  const [watermarkId, setWatermarkId] = useState('');
  const [watermarkTint, setWatermarkTint] = useState('');
  const [watermarkOpacity, setWatermarkOpacity] = useState(0.18);
  // When false, drag-to-align snapping is disabled — useful when the admin
  // wants to nudge a textbox to a position that's near another box but not
  // perfectly aligned. Persisted in localStorage so the preference sticks.
  const [snapEnabled, setSnapEnabled] = useState(() => {
    try { return localStorage.getItem('admin_snap_enabled') !== 'false'; } catch (_) { return true; }
  });
  // Ref the move handler reads — avoids re-binding the canvas listener every
  // time the toggle flips.
  const snapEnabledRef = useRef(snapEnabled);
  useEffect(() => {
    snapEnabledRef.current = snapEnabled;
    try { localStorage.setItem('admin_snap_enabled', snapEnabled ? 'true' : 'false'); } catch (_) {}
  }, [snapEnabled]);

  // Undo history — bounded stack of canvas JSON snapshots. Fabric's events
  // fire AFTER a mutation, so we keep `lastSnapshotRef` = the state that
  // existed BEFORE the most recent change. Each new change pushes that prior
  // state onto the stack, then updates `lastSnapshotRef` to the new state.
  // `restoringRef` suppresses both during a programmatic restore.
  const undoStackRef = useRef([]);
  const lastSnapshotRef = useRef(null);
  const restoringRef = useRef(false);
  const UNDO_LIMIT = 60;
  const FABRIC_PROPS = useMemo(() => [
    '_wrapperMeta', 'fieldId',
    '_isBrandingLogo', '_brandingNaturalW', '_brandingNaturalH', '_brandingFromUrl', '_brandingColor',
    '_isOverlayImage', '_overlayNaturalW', '_overlayNaturalH', '_overlayFromUrl', '_overlayConfig',
  ], []);
  const snapshotCanvas = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return null;
    try { return JSON.stringify(canvas.toJSON(FABRIC_PROPS)); } catch (_) { return null; }
  }, [FABRIC_PROPS]);
  const recordChange = useCallback(() => {
    if (restoringRef.current) return;
    const prior = lastSnapshotRef.current;
    const next = snapshotCanvas();
    if (!next) return;
    if (prior && prior !== next) {
      const stack = undoStackRef.current;
      stack.push(prior);
      if (stack.length > UNDO_LIMIT) stack.splice(0, stack.length - UNDO_LIMIT);
    }
    lastSnapshotRef.current = next;
  }, [snapshotCanvas]);
  const performUndo = useCallback(() => {
    const canvas = fabricRef.current;
    const stack = undoStackRef.current;
    if (!canvas || stack.length === 0) return;
    const prev = stack.pop();
    restoringRef.current = true;
    try {
      canvas.loadFromJSON(prev, () => {
        canvas.requestRenderAll();
        lastSnapshotRef.current = prev;
        restoringRef.current = false;
        forceRerender(v => v + 1);
      });
    } catch (_) {
      restoringRef.current = false;
    }
  }, []);
  // Bind Ctrl/Cmd+Z globally — but ignore the keystroke when the user is
  // typing in an input/textarea/contenteditable so we don't interfere with
  // native text-undo inside the side-panel inputs.
  useEffect(() => {
    function onKey(e) {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key !== 'z' && e.key !== 'Z') return;
      const t = e.target;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return;
      e.preventDefault();
      performUndo();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [performUndo]);

  useEffect(() => {
    (async () => {
      const [p, f, fc, af, pn, sh, hl, lp, brand, wms] = await Promise.all([
        api.get(`/products/GetProductAsAdmin/${id}`),
        api.get(`/fields/GetProductFieldsAsAdmin/${id}`),
        api.get('/files/GetAllFonts'),
        api.get('/miscellaneous/autofillableFields').catch(() => ({ data: { body: [] } })),
        api.get('/miscellaneous/peopleNames').catch(() => ({ data: { body: [] } })),
        api.get('/miscellaneous/shuls').catch(() => ({ data: { body: [] } })),
        api.get('/miscellaneous/halls').catch(() => ({ data: { body: [] } })),
        api.get('/miscellaneous/fieldLayoutPresets').catch(() => ({ data: { body: [] } })),
        api.get('/miscellaneous/branding').catch(() => ({ data: { body: null } })),
        api.get('/files/watermarks').catch(() => ({ data: { body: [] } })),
      ]);
      const productData = unwrap(p);
      const fieldsData = unwrap(f) || [];
      const fontCatalog = unwrap(fc) || [];

      await Promise.all(fontCatalog.map(font => ensureFont(font.name, font.path, font.weight)));
      // Wait until the browser's font system has fully integrated the new
      // faces — otherwise fabric can paint with a fallback before shaping
      // is ready, and the visual weight differs from the customer side.
      if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
        try { await document.fonts.ready; } catch (_) {}
      }

      setProduct(productData);
      setFields(fieldsData);
      setFonts(fontCatalog);
      setAutofillSlots(unwrap(af) || []);
      setPeopleNames(unwrap(pn) || []);
      setShuls(unwrap(sh) || []);
      setHalls(unwrap(hl) || []);
      setLayoutPresets(unwrap(lp) || []);
      const brandSetting = unwrap(brand);
      setBrandingImageUrl(brandSetting?.imageUrl || '');
      setBrandingColor(productData?.brandingColor || '#FFFFFF');
      // Hydrate overlays: prefer the new array, fall back to the legacy single
      // `overlayImage` for templates saved before this feature.
      const loadedOverlays = (productData?.overlayImages || []).filter(o => o && o.url);
      const overlaysFromData = loadedOverlays.length
        ? loadedOverlays
        : (productData?.overlayImage?.url ? [productData.overlayImage] : []);
      setOverlays(overlaysFromData.map(o => ({ ...o, _overlayId: o._overlayId || `o-${Math.random().toString(36).slice(2, 9)}` })));
      setWatermarks(unwrap(wms) || []);
      setWatermarkId(productData?.watermarkId ? String(productData.watermarkId) : '');
      setWatermarkTint(productData?.watermarkTint || '');
      setWatermarkOpacity(typeof productData?.watermarkOpacity === 'number' ? productData.watermarkOpacity : 0.18);
    })();
  }, [id]);

  // Scale the canvas to fit the available stage area so the admin sees the whole
  // template without scrolling. Re-applied on init + window resize.
  const fitCanvasToStage = useCallback(() => {
    const canvas = fabricRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return;
    const natW = canvas._naturalW || canvas.getWidth();
    const natH = canvas._naturalH || canvas.getHeight();
    const availW = stage.clientWidth;
    const availH = stage.clientHeight;
    if (!availW || !availH) return;
    const scale = Math.min(availW / natW, availH / natH);
    canvas.setDimensions({ width: natW * scale, height: natH * scale });
    canvas.setZoom(scale);
    canvas.requestRenderAll();
  }, []);

  useEffect(() => {
    if (!product) return;
    const first = fields[0] || {};
    const natW = first.canvasWidth || 800;
    const natH = first.canvasHeight || 1035;
    const canvas = new fabric.Canvas(canvasElRef.current, { width: natW, height: natH, backgroundColor: '#fff' });
    canvas._naturalW = natW;
    canvas._naturalH = natH;
    fabricRef.current = canvas;

    const bg = product.filesId?.path || product.filesId?.pathWithWatermark;
    if (bg) {
      fabric.Image.fromURL(bg, (img) => {
        const imgW = img.width;
        const imgH = img.height;
        // Resize the canvas to match the image's natural dimensions so the
        // image is never cut off. When the canvas dimensions change, scale
        // every existing textbox proportionally so its RELATIVE position,
        // size, and font size stay the same — a centered field stays
        // centered, an edge field stays at the edge.
        if (imgW && imgH && (imgW !== canvas._naturalW || imgH !== canvas._naturalH)) {
          const oldW = canvas._naturalW;
          const oldH = canvas._naturalH;
          const xRatio = imgW / oldW;
          const yRatio = imgH / oldH;
          const fontRatio = Math.sqrt(xRatio * yRatio);
          canvas.getObjects().forEach(obj => {
            if (obj.type === 'textbox') {
              obj.set({
                left: obj.left * xRatio,
                top: obj.top * yRatio,
                width: (obj.width || 200) * xRatio,
                fontSize: (obj.fontSize || 28) * fontRatio,
              });
              obj.setCoords();
            }
          });
          canvas._naturalW = imgW;
          canvas._naturalH = imgH;
          canvas.setDimensions({ width: imgW, height: imgH });
        }
        canvas.setBackgroundImage(img, () => {
          canvas.renderAll();
          fitCanvasToStage();
        });
      }, { crossOrigin: 'anonymous' });
    }

    for (const f of fields) {
      // Load EVERY saved style property — otherwise fabric falls back to its
      // defaults (e.g. fontWeight 'normal', opacity 1, scaleX 1) and the next
      // save overwrites the original style with those defaults. That's why
      // bold/italic/underline/scale settings appeared to reset between edits.
      const tb = new fabric.Textbox(f.originalText || f.text || '', {
        // Disable Fabric's internal render cache so glyphs taller than
        // fontSize × lineHeight (common with bold weights and Hebrew fonts
        // with extreme ascenders) aren't clipped by the cache buffer.
        objectCaching: false,
        left: f.left ?? 20,
        top: f.top ?? 20,
        width: f.width || f.maxWidth || 300,
        fontSize: f.fontSize || 28,
        fontFamily: f.fontFamily || 'Nexa',
        fontWeight: f.fontWeight || 'normal',
        fontStyle: f.fontStyle || 'normal',
        fill: f.fill || '#000',
        textAlign: f.textAlign || 'right',
        angle: f.angle || 0,
        scaleX: f.scaleX || 1,
        scaleY: f.scaleY || 1,
        charSpacing: f.charSpacing || 0,
        lineHeight: f.lineHeight || 1,
        originX: f.originX || 'left',
        originY: f.originY || 'top',
        direction: f.direction || 'rtl',
        underline: !!f.underline,
        overline: !!f.overline,
        linethrough: !!f.linethrough,
        opacity: f.opacity ?? 1,
        splitByGrapheme: !!f.splitByGrapheme,
        backgroundColor: f.backgroundColor || '',
        stroke: f.stroke || null,
        strokeWidth: f.strokeWidth ?? 1,
        flipX: !!f.flipX,
        flipY: !!f.flipY,
        skewX: f.skewX || 0,
        skewY: f.skewY || 0,
      });
      tb.fieldId = f._id;
      tb._wrapperMeta = pickMeta(f);
      canvas.add(tb);
    }

    // Selection handlers — fabric supports shift+click and rubber-band drag
    // out of the box, which create an ActiveSelection containing multiple
    // objects. We pick a "primary" textbox (the first one) for the side
    // panel inputs to display, but apply functions iterate over ALL active
    // textboxes so style edits hit every selected box at once.
    const onSelectChange = () => {
      const all = canvas.getActiveObjects() || [];
      const primary = all.find(o => o.type === 'textbox' && !o._isBrandingLogo && !o._isOverlayImage) || all[0];
      selectObject(primary);
      forceRerender(v => v + 1);
    };
    canvas.on('selection:created', onSelectChange);
    canvas.on('selection:updated', onSelectChange);
    canvas.on('selection:cleared', () => { setSelected(null); setSelectedSpecial(null); setDraftMeta({}); setDraftText(''); clearGuides(canvas); forceRerender(v => v + 1); });

    // When the user resizes/drags the textbox on the canvas, re-read its
    // current properties so the side-panel number inputs stay in sync.
    // We push an undo snapshot AFTER each commit (object:modified, object
    // added, object removed) — never on transient events like moving/scaling
    // mid-drag — so each Ctrl+Z rewinds exactly one user action.
    canvas.on('object:modified', () => { clearGuides(canvas); recordChange(); forceRerender(v => v + 1); });
    canvas.on('object:added', () => { recordChange(); });
    canvas.on('object:removed', () => { recordChange(); });
    canvas.on('object:scaling', () => forceRerender(v => v + 1));
    canvas.on('object:moving', (e) => {
      if (snapEnabledRef.current) snapOnMove(canvas, e);
      else clearGuides(canvas);
      forceRerender(v => v + 1);
    });
    canvas.on('mouse:up', () => clearGuides(canvas));

    fitCanvasToStage();

    // Seed undo history with the initial canvas state so the very first
    // user action has a previous state to roll back to.
    lastSnapshotRef.current = snapshotCanvas();
    undoStackRef.current = [];

    // Re-shape every textbox after fonts confirm ready, so admin and customer
    // reach identical glyph metrics on first paint.
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!fabricRef.current) return;
        fabricRef.current.getObjects().forEach(obj => {
          if (obj.type === 'textbox') {
            obj._textLines = [];
            obj._unwrappedTextLines = null;
            if (typeof obj.initDimensions === 'function') obj.initDimensions();
            obj.setCoords();
          }
        });
        fabricRef.current.requestRenderAll();
      }).catch(() => {});
    }

    const onResize = () => fitCanvasToStage();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      canvas.dispose();
      fabricRef.current = null;
    };
  }, [product, fields, fitCanvasToStage]);

  // Re-render the branding logo whenever the URL or chosen color changes.
  // When re-rendering after a color change, we PRESERVE the logo's current
  // canvas position — otherwise the helper would re-add it at the saved
  // position, undoing any drag/resize/rotate the admin has done in this session.
  // Also: if the logo was the active object (e.g. side panel open while the
  // admin drags through the color picker), re-select the new image afterwards
  // so the panel stays open and they can keep tweaking colors live.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !brandingImageUrl) return;
    const currentPos = readBrandingLogoPosition(canvas, canvas._naturalW, canvas._naturalH);
    const wasLogoActive = !!canvas.getActiveObject()?._isBrandingLogo;
    addBrandingLogoToCanvas(
      canvas,
      brandingImageUrl,
      brandingColor,
      canvas._naturalW,
      canvas._naturalH,
      currentPos || product?.brandingPosition || {},
      true,
    );
    if (wasLogoActive) {
      const fresh = canvas.getObjects().find(o => o._isBrandingLogo);
      if (fresh) {
        canvas.setActiveObject(fresh);
        canvas.requestRenderAll();
      }
    }
  }, [brandingImageUrl, brandingColor, product]);

  // Re-render every overlay whenever the array changes (urls, colors, removals).
  // Each overlay is keyed by `_overlayId` so changing one doesn't reset the
  // canvas position of the others. We preserve which overlay was selected so
  // dragging through the tint picker doesn't close the side panel.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    const wasOverlayActiveId = active?._isOverlayImage ? active._overlayId : null;

    // Remove any overlay on canvas whose id is no longer in the state array.
    const liveIds = new Set(overlays.map(o => o._overlayId));
    canvas.getObjects().filter(o => o._isOverlayImage && !liveIds.has(o._overlayId))
      .forEach(o => canvas.remove(o));

    (async () => {
      for (const o of overlays) {
        if (!o.url) continue;
        // Preserve the on-canvas position the admin may have nudged.
        const currentPos = readOverlayPosition(canvas, canvas._naturalW, canvas._naturalH, o._overlayId);
        const config = currentPos ? { ...o, ...currentPos } : o;
        await addOverlayToCanvas(canvas, config, canvas._naturalW, canvas._naturalH, true, o._overlayId);
      }
      if (wasOverlayActiveId) {
        const fresh = canvas.getObjects().find(o => o._isOverlayImage && o._overlayId === wasOverlayActiveId);
        if (fresh) {
          canvas.setActiveObject(fresh);
          canvas.requestRenderAll();
        }
      }
    })();
  }, [overlays, product]);

  async function uploadOverlay(file) {
    if (!file) return;
    setOverlayBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/files/UploadOverlayImage', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { url } = unwrap(r);
      const newOverlay = { url, color: '', _overlayId: `o-${Math.random().toString(36).slice(2, 9)}` };
      setOverlays(prev => [...prev, newOverlay]);
      setActiveOverlayId(newOverlay._overlayId);
    } finally { setOverlayBusy(false); }
  }

  function removeOverlayById(overlayId) {
    setOverlays(prev => prev.filter(o => o._overlayId !== overlayId));
    if (activeOverlayId === overlayId) setActiveOverlayId(null);
  }

  // Edit a property on whichever overlay is currently active in the side panel.
  function setActiveOverlayProp(prop, value) {
    if (!activeOverlayId) return;
    setOverlays(prev => prev.map(o => o._overlayId === activeOverlayId ? { ...o, [prop]: value } : o));
  }
  function setOverlayColor(color) { setActiveOverlayProp('color', color); }

  async function pickBrandingColor() {
    if (typeof window.EyeDropper === 'undefined') {
      alert('Your browser does not support the eyedropper. Try Chrome or Edge.');
      return;
    }
    try {
      const ed = new window.EyeDropper();
      const res = await ed.open();
      setBrandingColor(res.sRGBHex);
    } catch (_) { /* user cancelled */ }
  }

  async function pickOverlayTint() {
    if (typeof window.EyeDropper === 'undefined') {
      alert('Your browser does not support the eyedropper. Try Chrome or Edge.');
      return;
    }
    try {
      const ed = new window.EyeDropper();
      const res = await ed.open();
      setOverlayColor(res.sRGBHex);
    } catch (_) { /* user cancelled */ }
  }

  function selectLogoOnCanvas() {
    const canvas = fabricRef.current;
    const logo = canvas?.getObjects().find(o => o._isBrandingLogo);
    if (logo) {
      canvas.setActiveObject(logo);
      selectObject(logo);
      canvas.requestRenderAll();
    }
  }

  function selectOverlayOnCanvas() {
    const canvas = fabricRef.current;
    if (!canvas) return;
    // Prefer the active overlay if one's already selected; else the most
    // recently added (last in array); else any overlay on canvas.
    const targetId = activeOverlayId
      || (overlays.length ? overlays[overlays.length - 1]._overlayId : null);
    const ov = (targetId && canvas.getObjects().find(o => o._isOverlayImage && o._overlayId === targetId))
      || canvas.getObjects().find(o => o._isOverlayImage);
    if (ov) {
      canvas.setActiveObject(ov);
      selectObject(ov);
      canvas.requestRenderAll();
    }
  }

  // Find the currently-selected special object on the canvas.
  function getSpecialObject() {
    const canvas = fabricRef.current;
    if (!canvas) return null;
    if (selectedSpecial === 'logo') return canvas.getObjects().find(o => o._isBrandingLogo);
    if (selectedSpecial === 'overlay') {
      // Match the specific overlay by id when one is active. Falls back to
      // the first overlay if no id (e.g. legacy single-overlay flow).
      if (activeOverlayId) return canvas.getObjects().find(o => o._isOverlayImage && o._overlayId === activeOverlayId);
      return canvas.getObjects().find(o => o._isOverlayImage);
    }
    return null;
  }

  function specialScalePercent() {
    const canvas = fabricRef.current;
    const obj = getSpecialObject();
    if (!canvas || !obj) return 0;
    const w = obj.width * (obj.scaleX || 1);
    return (w / canvas._naturalW) * 100;
  }

  function setSpecialScale(percent) {
    const canvas = fabricRef.current;
    const obj = getSpecialObject();
    if (!canvas || !obj) return;
    const targetWidth = (percent / 100) * canvas._naturalW;
    const s = obj.width ? targetWidth / obj.width : 1;
    obj.set({ scaleX: s, scaleY: s });
    obj.setCoords();
    canvas.requestRenderAll();
    forceRerender(v => v + 1);
  }

  function setSpecialRotation(angle) {
    const canvas = fabricRef.current;
    const obj = getSpecialObject();
    if (!canvas || !obj) return;
    obj.set({ angle });
    obj.setCoords();
    canvas.requestRenderAll();
    forceRerender(v => v + 1);
  }

  function setSpecialPosition(axis, percent) {
    const canvas = fabricRef.current;
    const obj = getSpecialObject();
    if (!canvas || !obj) return;
    const naturalDim = axis === 'x' ? canvas._naturalW : canvas._naturalH;
    obj.set({ [axis === 'x' ? 'left' : 'top']: (percent / 100) * naturalDim });
    obj.setCoords();
    canvas.requestRenderAll();
    forceRerender(v => v + 1);
  }

  function pickMeta(f) {
    const m = {};
    for (const k of WRAPPER_KEYS) if (f[k] !== undefined) m[k] = f[k];
    return m;
  }

  function selectObject(obj) {
    if (!obj) return;
    if (obj._isBrandingLogo) {
      setSelectedSpecial('logo');
      setSelected(null);
      return;
    }
    if (obj._isOverlayImage) {
      setSelectedSpecial('overlay');
      setActiveOverlayId(obj._overlayId || null);
      setSelected(null);
      return;
    }
    setSelectedSpecial(null);
    setSelected(obj);
    setDraftMeta(obj._wrapperMeta || {});
    setDraftText(obj.text || '');
  }

  function addTextbox() {
    const canvas = fabricRef.current;
    const tb = new fabric.Textbox('Sample text', {
      objectCaching: false,
      left: 100, top: 100, width: 300,
      fontSize: 28, fontFamily: 'Nexa', fill: '#333',
      textAlign: 'right',
      direction: 'rtl',
    });
    // Pre-fill the new field's display order with the next available number so
    // adding fields one after another produces a sensible default sequence.
    const existingOrders = canvas.getObjects()
      .filter(o => o.type === 'textbox')
      .map(o => Number(o._wrapperMeta?.order) || 0);
    const nextOrder = (existingOrders.length ? Math.max(...existingOrders) : 0) + 1;
    tb._wrapperMeta = {
      originalText: 'Sample text',
      maxFontSize: 28, direction: 'rtl',
      maxWidth: 300,
      editable: true,
      prefilled: true,
      order: nextOrder,
    };
    canvas.add(tb);
    canvas.setActiveObject(tb);
    selectObject(tb);
  }

  function deleteSelected() {
    const canvas = fabricRef.current;
    const obj = canvas.getActiveObject();
    if (!obj) return;
    if (obj._isBrandingLogo || obj._isOverlayImage) return;
    if (obj.fieldId && window.confirm('Delete this saved field?')) {
      api.post('/fields/RemoveFieldsFromProduct', { fieldIds: [obj.fieldId] });
    }
    canvas.remove(obj);
    setSelected(null); setDraftMeta({}); setDraftText('');
  }

  async function deleteAllFields() {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const textBoxes = canvas.getObjects().filter(o => o.type === 'textbox');
    if (textBoxes.length === 0) {
      alert('No fields to delete.');
      return;
    }
    if (!window.confirm(`Delete all ${textBoxes.length} field${textBoxes.length === 1 ? '' : 's'} from this template? This cannot be undone.`)) return;
    const savedIds = textBoxes.map(o => o.fieldId).filter(Boolean);
    if (savedIds.length) {
      try {
        await api.post('/fields/RemoveFieldsFromProduct', { fieldIds: savedIds });
      } catch (e) {
        console.error('Failed to remove saved fields:', e);
        alert('Failed to delete saved fields from server. Aborting.');
        return;
      }
    }
    for (const obj of textBoxes) canvas.remove(obj);
    setSelected(null); setSelectedSpecial(null); setDraftMeta({}); setDraftText('');
    canvas.requestRenderAll();
  }

  // Returns every textbox currently selected on the canvas (so multi-select
  // edits can apply to all of them). Excludes the branding logo and overlay,
  // which have their own controls. Falls back to the `selected` ref if fabric
  // doesn't have an active selection (defensive).
  function getEditTargets() {
    const canvas = fabricRef.current;
    if (!canvas) return selected ? [selected] : [];
    const active = canvas.getActiveObjects() || [];
    const targets = active.filter(o => o && o.type === 'textbox' && !o._isBrandingLogo && !o._isOverlayImage);
    if (targets.length === 0 && selected) return [selected];
    return targets;
  }

  function applyProp(k, v) {
    const targets = getEditTargets();
    if (!targets.length) return;
    const needsRecompute = ['fontSize', 'fontFamily', 'fontWeight', 'text', 'width', 'charSpacing', 'lineHeight', 'direction'].includes(k);
    for (const t of targets) {
      t.set({ [k]: v });
      if (needsRecompute && typeof t.initDimensions === 'function') t.initDimensions();
      t.setCoords();
    }
    fabricRef.current?.requestRenderAll();
    forceRerender(x => x + 1);
  }

  // A family can have multiple weight variants uploaded under the same name
  // (Light/Medium/Bold). When the picker passes a specific weight, apply both
  // fontFamily and fontWeight; when called without weight (e.g. legacy callers
  // or "Nexa default"), only fontFamily changes — existing fontWeight stays
  // intact so old fields render the same as before.
  async function applyFont(fontName, fontWeight) {
    if (!getEditTargets().length) return;
    if (fontWeight) {
      const match = fonts.find(f => f.name === fontName && String(f.weight || '400') === String(fontWeight));
      if (match?.path) await ensureFont(match.name, match.path, match.weight || '400');
      applyProp('fontFamily', fontName);
      applyProp('fontWeight', String(fontWeight));
    } else {
      const match = fonts.find(f => f.name === fontName);
      if (match?.path) await ensureFont(match.name, match.path, match.weight || '400');
      applyProp('fontFamily', fontName);
    }
  }

  // Font preview state — while the admin hovers over a font in the dropdown,
  // we apply it to every selected textbox so they can see what it looks like,
  // then revert if they leave without clicking.
  const fontBeforeHoverRef = useRef(null);
  const [fontPickerOpen, setFontPickerOpen] = useState(false);
  // Independent state for the weight picker — same hover/preview/commit
  // pattern as the font picker, but only mutates fontWeight.
  const weightBeforeHoverRef = useRef(null);
  const [weightPickerOpen, setWeightPickerOpen] = useState(false);

  async function previewWeightHover(weight) {
    const targets = getEditTargets();
    if (!targets.length) return;
    if (weightBeforeHoverRef.current === null) {
      weightBeforeHoverRef.current = targets.map(t => ({ obj: t, weight: t.fontWeight || 'normal' }));
    }
    const w = String(weight);
    const fam = targets[0].fontFamily;
    const match = fonts.find(f => f.name === fam && String(f.weight || '400') === w);
    if (match?.path) await ensureFont(match.name, match.path, match.weight || '400');
    for (const t of targets) {
      t.set({ fontWeight: w });
      if (typeof t.initDimensions === 'function') t.initDimensions();
      t.setCoords();
    }
    fabricRef.current?.requestRenderAll();
  }

  function cancelWeightPreview() {
    if (!weightBeforeHoverRef.current) return;
    for (const { obj, weight } of weightBeforeHoverRef.current) {
      obj.set({ fontWeight: weight });
      if (typeof obj.initDimensions === 'function') obj.initDimensions();
      obj.setCoords();
    }
    fabricRef.current?.requestRenderAll();
    weightBeforeHoverRef.current = null;
  }

  async function commitWeightPick(weight) {
    weightBeforeHoverRef.current = null;
    setWeightPickerOpen(false);
    await applyWeight(weight);
  }

  async function previewFontHover(fontName, fontWeight) {
    const targets = getEditTargets();
    if (!targets.length) return;
    if (fontBeforeHoverRef.current === null) {
      // Capture the original font + weight of every target so we can restore
      // them all on cancel even when there's a multi-select.
      fontBeforeHoverRef.current = targets.map(t => ({
        obj: t,
        font: t.fontFamily || 'Nexa',
        weight: t.fontWeight || 'normal',
      }));
    }
    const match = fontWeight
      ? fonts.find(f => f.name === fontName && String(f.weight || '400') === String(fontWeight))
      : fonts.find(f => f.name === fontName);
    if (match?.path) await ensureFont(match.name, match.path, match.weight || '400');
    for (const t of targets) {
      const next = { fontFamily: fontName };
      if (fontWeight) next.fontWeight = String(fontWeight);
      t.set(next);
      if (typeof t.initDimensions === 'function') t.initDimensions();
      t.setCoords();
    }
    fabricRef.current?.requestRenderAll();
  }

  function cancelFontPreview() {
    if (!fontBeforeHoverRef.current) return;
    for (const { obj, font, weight } of fontBeforeHoverRef.current) {
      obj.set({ fontFamily: font, fontWeight: weight });
      if (typeof obj.initDimensions === 'function') obj.initDimensions();
      obj.setCoords();
    }
    fabricRef.current?.requestRenderAll();
    fontBeforeHoverRef.current = null;
  }

  async function commitFontPick(fontName, fontWeight) {
    fontBeforeHoverRef.current = null;
    setFontPickerOpen(false);
    await applyFont(fontName, fontWeight);
  }

  // Text editing always applies to a SINGLE primary box — typing into the
  // textarea rarely makes sense for many fields at once (each holds its own
  // distinct content). Multi-select still affects style/font/color via the
  // other apply functions.
  function applyText(v) {
    if (!selected) return;
    const dir = selected._wrapperMeta?.direction || 'rtl';
    const text = dir === 'rtl' ? swapHebrewQuotes(v) : v;
    selected.set({ text });
    if (typeof selected.initDimensions === 'function') selected.initDimensions();
    selected.setCoords();
    selected._wrapperMeta = { ...(selected._wrapperMeta || {}), originalText: text };
    setDraftText(text);
    setDraftMeta(selected._wrapperMeta);
    fabricRef.current?.requestRenderAll();
  }

  function applyMeta(k, v) {
    const targets = getEditTargets();
    if (!targets.length) return;
    for (const t of targets) {
      t._wrapperMeta = { ...(t._wrapperMeta || {}), [k]: v };
    }
    // The sidebar reflects the primary's metadata (matches what user sees in
    // the inputs); update it so the field stays in sync.
    if (selected) setDraftMeta(selected._wrapperMeta || {});
  }

  // `order` is the field's display index in the customer-facing sidebar.
  // The admin sets it via the "Display order in customer editor" input on
  // each field. We always prefer the admin's typed value over any auto
  // index, so manual order numbers stick across "Save Template" clicks.
  // Falls back to canvas-stack position only when no order has been set.
  function buildFieldBody(obj, orderIndex) {
    const fabricProps = obj.toObject();
    const meta = obj._wrapperMeta || {};
    const canvas = fabricRef.current;
    const w = canvas._naturalW || canvas.getWidth();
    const h = canvas._naturalH || canvas.getHeight();
    let order;
    if (Number.isFinite(Number(meta.order))) {
      order = Number(meta.order);
    } else if (Number.isFinite(orderIndex)) {
      order = orderIndex;
    } else {
      const all = canvas.getObjects().filter(o => o.type === 'textbox');
      const idx = all.indexOf(obj);
      order = idx >= 0 ? idx : 0;
    }
    const body = {
      productId: id,
      autocompleteLabel: meta.autocompleteLabel || '',
      autofillableField: meta.autofillableField || '',
      originalText: meta.originalText || obj.text,
      originalTextLength: (meta.originalText || obj.text || '').length,
      originalLinesLength: (meta.originalText || obj.text || '').split('\n').length,
      multiLine: !!meta.multiLine,
      editable: meta.editable !== false,
      // Defaults to true so fields coming from older layout presets (where
      // _wrapperMeta has no prefilled key at all) still get the checkbox on.
      // Admin can explicitly turn it off by unchecking — that stores false.
      prefilled: meta.prefilled !== false,
      isUpperCase: !!meta.isUpperCase,
      direction: meta.direction || 'rtl',
      horizontallyCentered: !!meta.horizontallyCentered,
      maxFontSize: Number(meta.maxFontSize || obj.fontSize),
      maxWidth: Number(meta.maxWidth || obj.width),
      order,
      canvasWidth: w, canvasHeight: h,
      originalImageWidth: w, originalImageHeight: h,
    };
    for (const k of FABRIC_KEYS) if (fabricProps[k] !== undefined) body[k] = fabricProps[k];
    return body;
  }

  async function saveField() {
    if (!selected) return;
    const body = buildFieldBody(selected);
    if (selected.fieldId) {
      body._id = selected.fieldId;
      const r = await api.post('/fields/UpdateField', body);
      selected.fieldId = unwrap(r)._id;
    } else {
      const r = await api.post('/fields/AddFieldToProduct', body);
      selected.fieldId = unwrap(r)._id;
    }
    alert('Field saved.');
  }

  // Properties copied from one textbox to another via Copy Style / Paste Style.
  // We leave autofill alone — that is unique to each field.
  const STYLE_KEYS = [
    'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'fill', 'textAlign',
    'angle', 'charSpacing', 'lineHeight', 'underline', 'overline', 'linethrough',
    'opacity', 'stroke', 'strokeWidth', 'direction', 'scaleX', 'scaleY',
  ];
  const STYLE_META_KEYS = ['direction', 'maxFontSize', 'isUpperCase', 'horizontallyCentered'];

  function copyStyle() {
    if (!selected) return;
    const style = {};
    for (const k of STYLE_KEYS) style[k] = selected[k];
    const metaStyle = {};
    for (const k of STYLE_META_KEYS) {
      if (selected._wrapperMeta?.[k] !== undefined) metaStyle[k] = selected._wrapperMeta[k];
    }
    setClipboardStyle({ fabric: style, meta: metaStyle });
  }

  function pasteStyle() {
    if (!clipboardStyle) return;
    const targets = getEditTargets();
    if (!targets.length) return;
    for (const t of targets) {
      t.set(clipboardStyle.fabric);
      t._wrapperMeta = { ...(t._wrapperMeta || {}), ...clipboardStyle.meta };
      if (typeof t.initDimensions === 'function') t.initDimensions();
      t.setCoords();
    }
    fabricRef.current?.requestRenderAll();
    if (selected) setDraftMeta(selected._wrapperMeta || {});
    forceRerender(v => v + 1);
  }

  // Photoshop-style alignment. With 2+ objects selected, align them relative
  // to the union bounding box of the selection. With a single object, align it
  // to the canvas. Uses absolute bounding rects so rotation/scale are handled
  // correctly. ActiveSelection is discarded during the move (otherwise object
  // .left/.top are relative to the group), then restored at the end.
  function alignSelected(mode) {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    let objs = [];
    let restoreSelection = false;
    if (active && active.type === 'activeSelection') {
      objs = active.getObjects().slice();
      restoreSelection = true;
      canvas.discardActiveObject();
    } else if (active) {
      objs = [active];
    }
    if (!objs.length) return;

    const natW = canvas._naturalW || canvas.getWidth();
    const natH = canvas._naturalH || canvas.getHeight();
    let refLeft, refTop, refRight, refBottom;
    if (objs.length >= 2) {
      let l = Infinity, t = Infinity, r = -Infinity, b = -Infinity;
      for (const o of objs) {
        const bb = o.getBoundingRect(true, true);
        l = Math.min(l, bb.left);
        t = Math.min(t, bb.top);
        r = Math.max(r, bb.left + bb.width);
        b = Math.max(b, bb.top + bb.height);
      }
      refLeft = l; refTop = t; refRight = r; refBottom = b;
    } else {
      refLeft = 0; refTop = 0; refRight = natW; refBottom = natH;
    }

    for (const o of objs) {
      const bb = o.getBoundingRect(true, true);
      let dx = 0, dy = 0;
      if (mode === 'left') dx = refLeft - bb.left;
      else if (mode === 'centerH') dx = (refLeft + refRight) / 2 - (bb.left + bb.width / 2);
      else if (mode === 'right') dx = refRight - (bb.left + bb.width);
      else if (mode === 'top') dy = refTop - bb.top;
      else if (mode === 'centerV') dy = (refTop + refBottom) / 2 - (bb.top + bb.height / 2);
      else if (mode === 'bottom') dy = refBottom - (bb.top + bb.height);
      o.set({ left: o.left + dx, top: o.top + dy });
      o.setCoords();
    }

    if (restoreSelection) {
      const sel = new fabric.ActiveSelection(objs, { canvas });
      canvas.setActiveObject(sel);
    }
    canvas.fire('object:modified');
    canvas.requestRenderAll();
    forceRerender(v => v + 1);
  }

  // Distribute the centers of 3+ selected objects evenly between the outermost
  // two on the chosen axis. Outermost objects stay put.
  function distributeSelected(axis) {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    let objs = [];
    let restoreSelection = false;
    if (active && active.type === 'activeSelection') {
      objs = active.getObjects().slice();
      restoreSelection = true;
      canvas.discardActiveObject();
    } else if (active) {
      objs = [active];
    }
    if (objs.length < 3) return;

    const items = objs.map(o => ({ o, bb: o.getBoundingRect(true, true) }));
    if (axis === 'h') {
      items.sort((a, b) => (a.bb.left + a.bb.width / 2) - (b.bb.left + b.bb.width / 2));
      const firstC = items[0].bb.left + items[0].bb.width / 2;
      const lastC = items[items.length - 1].bb.left + items[items.length - 1].bb.width / 2;
      const step = (lastC - firstC) / (items.length - 1);
      for (let i = 1; i < items.length - 1; i++) {
        const target = firstC + step * i;
        const cur = items[i].bb.left + items[i].bb.width / 2;
        items[i].o.set({ left: items[i].o.left + (target - cur) });
        items[i].o.setCoords();
      }
    } else {
      items.sort((a, b) => (a.bb.top + a.bb.height / 2) - (b.bb.top + b.bb.height / 2));
      const firstC = items[0].bb.top + items[0].bb.height / 2;
      const lastC = items[items.length - 1].bb.top + items[items.length - 1].bb.height / 2;
      const step = (lastC - firstC) / (items.length - 1);
      for (let i = 1; i < items.length - 1; i++) {
        const target = firstC + step * i;
        const cur = items[i].bb.top + items[i].bb.height / 2;
        items[i].o.set({ top: items[i].o.top + (target - cur) });
        items[i].o.setCoords();
      }
    }

    if (restoreSelection) {
      const sel = new fabric.ActiveSelection(objs, { canvas });
      canvas.setActiveObject(sel);
    }
    canvas.fire('object:modified');
    canvas.requestRenderAll();
    forceRerender(v => v + 1);
  }

  function centerAllHorizontally() {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const natW = canvas._naturalW || canvas.getWidth();
    const textBoxes = canvas.getObjects().filter(o => o.type === 'textbox');
    for (const tb of textBoxes) {
      tb.set({ textAlign: 'center' });
      if (typeof tb.initDimensions === 'function') tb.initDimensions();
      const w = tb.getScaledWidth();
      tb.set({ left: (natW - w) / 2 });
      tb._wrapperMeta = { ...(tb._wrapperMeta || {}), horizontallyCentered: true };
      tb.setCoords();
    }
    canvas.requestRenderAll();
    if (selected) setDraftMeta(selected._wrapperMeta || {});
    forceRerender(v => v + 1);
  }

  // Snapshot every textbox on the canvas into a plain-object preset payload.
  // We strip out DB ids (_id, fieldId) so loading the preset on a different
  // template creates fresh Field documents instead of colliding with existing ones.
  function snapshotCurrentLayout() {
    const canvas = fabricRef.current;
    if (!canvas) return null;
    const natW = canvas._naturalW || canvas.getWidth();
    const natH = canvas._naturalH || canvas.getHeight();
    const textBoxes = canvas.getObjects().filter(o => o.type === 'textbox');
    const fields = textBoxes.map(obj => {
      const fabricProps = obj.toObject();
      const snap = { _wrapperMeta: { ...(obj._wrapperMeta || {}) } };
      for (const k of FABRIC_KEYS) if (fabricProps[k] !== undefined) snap[k] = fabricProps[k];
      return snap;
    });
    return { canvasWidth: natW, canvasHeight: natH, fields };
  }

  async function saveLayoutAsPreset() {
    const name = window.prompt('Name this layout preset (e.g. "Bris — 8 fields centered"):');
    if (!name || !name.trim()) return;
    const trimmed = name.trim();
    const snap = snapshotCurrentLayout();
    if (!snap || !snap.fields.length) {
      alert('No text boxes on the canvas to save.');
      return;
    }
    // If a preset with this exact name already exists, overwrite it (case-
    // insensitive match) rather than creating a duplicate.
    const existing = layoutPresets.find(p => p.name.trim().toLowerCase() === trimmed.toLowerCase());
    let description = existing?.description || '';
    if (existing) {
      const ok = window.confirm(`A preset named "${existing.name}" already exists. Overwrite it with the current layout?`);
      if (!ok) return;
    } else {
      description = window.prompt('Optional description / notes:', '') || '';
    }
    try {
      if (existing) {
        const r = await api.post('/miscellaneous/fieldLayoutPresets/update', {
          _id: existing._id, name: trimmed, description, ...snap,
        });
        const updated = unwrap(r);
        setLayoutPresets(prev => prev.map(p => String(p._id) === String(existing._id) ? updated : p));
        setPresetMsg(`✓ Updated layout "${updated.name}" (${snap.fields.length} fields).`);
      } else {
        const r = await api.post('/miscellaneous/fieldLayoutPresets', {
          name: trimmed, description, ...snap,
        });
        const created = unwrap(r);
        setLayoutPresets(prev => [...prev, created]);
        setPresetMsg(`✓ Saved layout "${created.name}" (${snap.fields.length} fields).`);
      }
    } catch (e) {
      console.error(e);
      setPresetMsg('Failed to save preset.');
    }
  }

  // Apply a saved preset by rebuilding its textboxes on the current canvas.
  // If the current canvas has textboxes already, ask first — the preset replaces them.
  async function applyLayoutPreset(presetId) {
    if (!presetId) return;
    const preset = layoutPresets.find(p => String(p._id) === String(presetId));
    if (!preset) return;
    const canvas = fabricRef.current;
    if (!canvas) return;
    const existing = canvas.getObjects().filter(o => o.type === 'textbox');
    if (existing.length > 0) {
      const go = window.confirm(`The canvas has ${existing.length} existing text box${existing.length === 1 ? '' : 'es'}. Replace them with "${preset.name}"?`);
      if (!go) return;
      for (const obj of existing) canvas.remove(obj);
    }

    // Make sure every font referenced in the preset is loaded before we
    // construct textboxes — otherwise Fabric measures with the fallback font
    // and the boxes render with wrong dimensions (or appear blank).
    const neededFonts = Array.from(new Set((preset.fields || []).map(f => f.fontFamily).filter(Boolean)));
    await Promise.all(neededFonts.map(name => {
      const match = fonts.find(ff => ff.name === name);
      if (match?.path) return ensureFont(match.name, match.path, match.weight || '400');
      return null;
    }));

    // Center the preset's group of fields on this canvas: presets store
    // absolute coordinates from the canvas they were saved on, so without
    // re-centering they land off-center (or off-canvas) on a different image.
    // We compute the bounding box of all fields, then translate every field
    // by the offset that lands the box's center on the canvas's center.
    let dx = 0, dy = 0;
    if ((preset.fields || []).length > 0) {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      for (const f of preset.fields) {
        const left = Number(f.left) || 0;
        const top = Number(f.top) || 0;
        const w = (Number(f.width) || 200) * (Number(f.scaleX) || 1);
        const h = (Number(f.fontSize) || 24) * (Number(f.lineHeight) || 1) * (Number(f.scaleY) || 1) * 1.2;
        if (left < minX) minX = left;
        if (top < minY) minY = top;
        if (left + w > maxX) maxX = left + w;
        if (top + h > maxY) maxY = top + h;
      }
      const groupCx = (minX + maxX) / 2;
      const groupCy = (minY + maxY) / 2;
      const canvasCx = (canvas._naturalW || canvas.getWidth()) / 2;
      const canvasCy = (canvas._naturalH || canvas.getHeight()) / 2;
      dx = canvasCx - groupCx;
      dy = canvasCy - groupCy;
    }

    let firstNew = null;
    for (const f of (preset.fields || [])) {
      const meta = f._wrapperMeta || {};
      const tb = new fabric.Textbox(f.text || meta.originalText || '', {
        objectCaching: false,
        left: (f.left || 20) + dx, top: (f.top || 20) + dy,
        width: f.width || 300,
        fontSize: f.fontSize || 28,
        fontFamily: f.fontFamily || 'Nexa',
        fontWeight: f.fontWeight || 'normal',
        fontStyle: f.fontStyle || 'normal',
        fill: f.fill || '#000',
        textAlign: f.textAlign || 'right',
        angle: f.angle || 0,
        scaleX: f.scaleX || 1,
        scaleY: f.scaleY || 1,
        charSpacing: f.charSpacing || 0,
        lineHeight: f.lineHeight || 1,
        direction: f.direction || 'rtl',
        underline: !!f.underline,
        opacity: f.opacity ?? 1,
        splitByGrapheme: !!f.splitByGrapheme,
      });
      tb._wrapperMeta = { ...meta };
      if (typeof tb.initDimensions === 'function') tb.initDimensions();
      tb.setCoords();
      canvas.add(tb);
      if (!firstNew) firstNew = tb;
    }
    if (firstNew) {
      canvas.setActiveObject(firstNew);
      selectObject(firstNew);
    }
    canvas.renderAll();
    setPresetMsg(`✓ Loaded "${preset.name}" (${(preset.fields || []).length} field${preset.fields.length === 1 ? '' : 's'}) — click "Save Template" to persist.`);
  }

  async function saveAllFields() {
    const canvas = fabricRef.current;
    if (!canvas) return;
    setSavingAll(true);
    setSaveAllMsg('');
    try {
      const brandingPosition = readBrandingLogoPosition(canvas, canvas._naturalW, canvas._naturalH);
      const productPatch = {
        _id: id,
        brandingColor,
        watermarkId: watermarkId || null,
        watermarkTint,
        watermarkOpacity,
      };
      if (brandingPosition) productPatch.brandingPosition = brandingPosition;
      // Build overlayImages array from current state, applying each overlay's
      // live canvas position so drags/resizes/rotations are persisted.
      productPatch.overlayImages = overlays
        .filter(o => o && o.url)
        .map(o => {
          const pos = readOverlayPosition(canvas, canvas._naturalW, canvas._naturalH, o._overlayId);
          // Strip the session-local _overlayId before saving — the server
          // schema doesn't define it, and the IDs are regenerated on load.
          const { _overlayId, ...rest } = o;
          return { ...rest, ...(pos || {}) };
        });
      // Clear the legacy single-overlay field so old data doesn't shadow the
      // new array on next load.
      productPatch.overlayImage = null;
      await api.post('/products/UpdateProduct', productPatch);
    } catch (e) {
      console.error('Failed to save product branding/overlay:', e);
      setSaveAllMsg('Save warning: branding/overlay did not save (' + (e?.response?.data?.body || e.message) + ')');
    }
    const textBoxes = canvas.getObjects().filter(o => o.type === 'textbox');

    // Delete any fields that exist in the DB for this product but are no
    // longer on the canvas (e.g., the admin removed them, replaced them via
    // a layout preset, or loaded a different layout). Without this, old
    // fields would stay in the DB and double up on the customer side.
    try {
      const dbFields = unwrap(await api.get(`/fields/GetProductFieldsAsAdmin/${id}`)) || [];
      const canvasFieldIds = new Set(textBoxes.map(o => o.fieldId).filter(Boolean).map(String));
      const stale = dbFields.filter(f => !canvasFieldIds.has(String(f._id))).map(f => f._id);
      if (stale.length) {
        await api.post('/fields/RemoveFieldsFromProduct', { fieldIds: stale });
      }
    } catch (e) {
      console.error('Failed to clean up stale fields:', e);
    }

    let ok = 0, fail = 0;
    for (let i = 0; i < textBoxes.length; i++) {
      const obj = textBoxes[i];
      try {
        const body = buildFieldBody(obj, i);
        if (obj.fieldId) {
          body._id = obj.fieldId;
          const r = await api.post('/fields/UpdateField', body);
          obj.fieldId = unwrap(r)._id;
        } else {
          const r = await api.post('/fields/AddFieldToProduct', body);
          obj.fieldId = unwrap(r)._id;
        }
        ok++;
      } catch (e) {
        console.error('Save failed for field', obj, e);
        fail++;
      }
    }
    setSavingAll(false);
    setSaveAllMsg(fail === 0 ? `✓ Saved all ${ok} fields.` : `Saved ${ok}, ${fail} failed.`);
  }

  // Eyedropper: uses the native EyeDropper API (Chrome 95+, Edge 95+, Opera 81+)
  async function pickColor() {
    if (!selected) return;
    if (typeof window.EyeDropper === 'undefined') {
      alert('Your browser does not support the eyedropper. Try Chrome or Edge.');
      return;
    }
    try {
      const ed = new window.EyeDropper();
      const res = await ed.open();
      applyProp('fill', res.sRGBHex);
    } catch (_) { /* user cancelled */ }
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  async function generatePreviews() {
    const canvas = fabricRef.current;
    if (!canvas) return null;
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    await new Promise(r => setTimeout(r, 60));

    const natW = canvas._naturalW;
    const natH = canvas._naturalH;
    const displayScale = canvas.getZoom();

    // Cap the preview dimensions so the encoded data URL stays reasonable.
    // 1600 px on the longer side is plenty for catalog thumbnails / customer
    // editor previews while keeping the JSON payload well under the backend
    // body limit. Use JPEG with high quality for the watermarked version
    // (much smaller than PNG); keep the clean thumbnail as JPEG too.
    const MAX_PREVIEW_DIM = 1600;
    const exportScale = Math.min(1, MAX_PREVIEW_DIM / Math.max(natW, natH));
    const exportW = Math.round(natW * exportScale);
    const exportH = Math.round(natH * exportScale);

    canvas.setDimensions({ width: exportW, height: exportH });
    canvas.setZoom(exportScale);
    canvas.requestRenderAll();
    await new Promise(r => setTimeout(r, 60));

    const cleanDataUrl = canvas.toDataURL({ format: 'jpeg', quality: 0.88 });

    // restore display size
    canvas.setDimensions({ width: natW * displayScale, height: natH * displayScale });
    canvas.setZoom(displayScale);
    canvas.requestRenderAll();

    let watermarkUrl = null;
    try {
      const r = await api.get('/miscellaneous/GetDefaultWatermarkId');
      const wmId = unwrap(r);
      const list = await api.get('/files/watermarks');
      const watermarks = unwrap(list) || [];
      const wm = wmId ? watermarks.find(w => String(w._id) === String(wmId)) : watermarks[0];
      if (wm?.path) watermarkUrl = wm.path;
    } catch (_) {}

    let watermarkedDataUrl = cleanDataUrl;
    if (watermarkUrl) {
      try {
        const [bgImg, wmImg] = await Promise.all([loadImage(cleanDataUrl), loadImage(watermarkUrl)]);
        const off = document.createElement('canvas');
        off.width = bgImg.width;
        off.height = bgImg.height;
        const ctx = off.getContext('2d');
        ctx.drawImage(bgImg, 0, 0);

        ctx.globalAlpha = 0.4;
        // Fit the watermark to span the whole image (one stretched copy)
        const fitScale = Math.min(off.width / wmImg.width, off.height / wmImg.height);
        const drawW = wmImg.width * fitScale;
        const drawH = wmImg.height * fitScale;
        const x = (off.width - drawW) / 2;
        const y = (off.height - drawH) / 2;
        ctx.drawImage(wmImg, x, y, drawW, drawH);
        watermarkedDataUrl = off.toDataURL('image/jpeg', 0.88);
      } catch (e) {
        console.warn('Watermark generation failed:', e);
      }
    }

    return { cleanDataUrl, watermarkedDataUrl };
  }

  async function handleGenerateAndSave() {
    setGenerating(true);
    setGenerateMsg('');
    try {
      const result = await generatePreviews();
      if (!result) throw new Error('Canvas not ready');
      await api.post('/products/SaveGeneratedAssets', {
        productId: id,
        thumbnail: result.cleanDataUrl,
        watermarked: result.watermarkedDataUrl,
      });
      setGenerateMsg('✓ Preview & watermarked version saved.');
    } catch (e) {
      console.error('Generate preview failed:', e);
      const reason = e?.response?.data?.body || e?.message || 'unknown error';
      setGenerateMsg(`Failed to generate preview: ${reason}`);
    } finally {
      setGenerating(false);
    }
  }

  // Number input handler: uses blur (or Enter) to commit, so typing multi-digit
  // values like "28" doesn't briefly set fontSize to 2.
  // Number input that commits LIVE on every change — so arrow keys, spinner
  // clicks, and typed digits all push the new value onto the canvas immediately.
  // Empty string is tolerated (user clearing the field) without snapping back.
  function NumberProp({ label, propKey, min, max, step = 1, fallback }) {
    const current = selected?.[propKey];
    const displayValue = current !== undefined && current !== null ? current : (fallback ?? '');
    const [draft, setDraft] = useState(String(displayValue));
    useEffect(() => { setDraft(String(displayValue)); }, [displayValue, selected]);
    function commit(v) {
      if (v === '' || v === '-') return;
      const n = Number(v);
      if (!Number.isFinite(n)) return;
      const clamped = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, n));
      applyProp(propKey, clamped);
    }
    return (
      <div className="prop" style={{ flex: 1 }}>
        <label>{label}</label>
        <input
          type="number"
          value={draft}
          step={step}
          min={min}
          max={max}
          onChange={e => { setDraft(e.target.value); commit(e.target.value); }}
          onBlur={() => { if (draft === '' || draft === '-') setDraft(String(displayValue)); }}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        />
      </div>
    );
  }

  function NumberMeta({ label, metaKey, min, max, fallback }) {
    const current = draftMeta[metaKey];
    const displayValue = current !== undefined && current !== null ? current : (fallback ?? '');
    const [draft, setDraft] = useState(String(displayValue));
    useEffect(() => { setDraft(String(displayValue)); }, [displayValue]);
    function commit(v) {
      if (v === '' || v === '-') return;
      const n = Number(v);
      if (!Number.isFinite(n)) return;
      const clamped = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, n));
      applyMeta(metaKey, clamped);
    }
    return (
      <div className="prop" style={{ flex: 1 }}>
        <label>{label}</label>
        <input
          type="number"
          value={draft}
          min={min}
          max={max}
          onChange={e => { setDraft(e.target.value); commit(e.target.value); }}
          onBlur={() => { if (draft === '' || draft === '-') setDraft(String(displayValue)); }}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        />
      </div>
    );
  }

  // Group every (name, weight) catalog entry by family name, so the picker
  // shows one row per family. weightsByName lets the side-panel weight
  // selector enumerate just the weights available for the chosen family.
  const weightsByName = (() => {
    const map = new Map();
    for (const f of fonts) {
      const w = String(f.weight || '400');
      if (!map.has(f.name)) map.set(f.name, new Set());
      map.get(f.name).add(w);
    }
    const out = {};
    for (const [k, set] of map.entries()) {
      out[k] = [...set].sort((a, b) => Number(a) - Number(b));
    }
    return out;
  })();

  // Pick a "representative" entry per family for the picker row preview —
  // prefer Regular (400), else the closest weight to 400, else the first.
  function representativeWeight(name) {
    const ws = weightsByName[name] || ['400'];
    if (ws.includes('400')) return '400';
    return ws.slice().sort((a, b) => Math.abs(Number(a) - 400) - Math.abs(Number(b) - 400))[0];
  }

  const families = (() => {
    const seen = new Set();
    const out = [];
    for (const f of fonts) {
      if (seen.has(f.name)) continue;
      seen.add(f.name);
      out.push({ name: f.name, _id: f._id });
    }
    return out;
  })();

  const filteredFamilies = families.filter(f =>
    !fontSearch || f.name.toLowerCase().includes(fontSearch.toLowerCase())
  );

  const WEIGHT_LABEL = { '100': 'Thin', '200': 'Extra Light', '300': 'Light', '400': 'Regular', '500': 'Medium', '600': 'Semi Bold', '700': 'Bold', '800': 'Extra Bold', '900': 'Black', 'normal': 'Regular', 'bold': 'Bold' };
  // Display the numeric weight in parens when it adds info — for legacy
  // 'normal' / 'bold' values, the label alone is already clear.
  const weightLabel = w => {
    const label = WEIGHT_LABEL[w] || w;
    return /^\d+$/.test(String(w)) ? `${label} (${w})` : label;
  };

  // Apply just the weight to the selected textbox(es). Used by the new
  // separate weight selector under the font picker. fontFamily is left
  // alone so weight changes don't accidentally swap the family.
  async function applyWeight(weight) {
    const targets = getEditTargets();
    if (!targets.length) return;
    const w = String(weight);
    const fam = targets[0].fontFamily;
    const match = fonts.find(f => f.name === fam && String(f.weight || '400') === w);
    if (match?.path) await ensureFont(match.name, match.path, match.weight || '400');
    applyProp('fontWeight', w);
  }

  // Figure out which preset list to show based on the selected autofill slot name.
  // Matching is substring + case-insensitive so slots like "Chusen's Shul" or
  // "Kallah's Hall" still resolve to the right list.
  function getPresetsForSlot(slotName) {
    if (!slotName) return { kind: 'none', items: [] };
    const s = slotName.toLowerCase();
    if (s.includes('shul')) return { kind: 'shul', items: shuls.map(x => x.name).filter(Boolean) };
    if (s.includes('hall') || s.includes('venue')) return { kind: 'hall', items: halls.map(x => x.name).filter(Boolean) };
    if (s.includes('name') || s.includes('parent') || s.includes('chusen') || s.includes('kallah')) {
      return { kind: 'people', items: peopleNames.map(p => [p.firstName, p.lastName].filter(Boolean).join(' ')).filter(Boolean) };
    }
    return { kind: 'none', items: [] };
  }
  const presetInfo = getPresetsForSlot(draftMeta.autofillableField);

  async function addPresetOnTheFly() {
    if (!draftText || !presetInfo.kind || presetInfo.kind === 'none') return;
    try {
      if (presetInfo.kind === 'shul') {
        const r = await api.post('/miscellaneous/shuls', { name: draftText });
        const created = unwrap(r);
        setShuls(prev => [...prev, created]);
      } else if (presetInfo.kind === 'hall') {
        const r = await api.post('/miscellaneous/halls', { name: draftText });
        const created = unwrap(r);
        setHalls(prev => [...prev, created]);
      } else if (presetInfo.kind === 'people') {
        const parts = draftText.trim().split(/\s+/);
        const firstName = parts[0] || '';
        const lastName = parts.slice(1).join(' ');
        const r = await api.post('/miscellaneous/peopleNames', { firstName, lastName });
        const created = unwrap(r);
        setPeopleNames(prev => [...prev, created]);
      }
      alert(`Added "${draftText}" to the ${presetInfo.kind} preset list.`);
    } catch (e) {
      alert('Failed to add preset.');
    }
  }

  return (
    <>
      <h1>Lay out fields · {product?.name}</h1>
      <div className="toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn small" onClick={addTextbox}>+ Add Text</button>
        <button className="btn small secondary" onClick={saveField} disabled={!selected}>Save selected</button>
        <button className="btn small" onClick={saveAllFields} disabled={savingAll} style={{ background: '#198754', color: '#fff' }}>
          {savingAll ? 'Saving…' : 'Save Template (all fields)'}
        </button>
        <button className="btn small danger" onClick={deleteSelected} disabled={!selected}>Delete selected</button>
        <button className="btn small danger" onClick={deleteAllFields} title="Remove every text field from this template">Delete all fields</button>
        <span style={{ width: 1, height: 22, background: '#ddd' }} />
        <button className="btn small secondary" onClick={copyStyle} disabled={!selected} title="Copy this textbox's style to paste on another">
          Copy style
        </button>
        <button className="btn small secondary" onClick={pasteStyle} disabled={!selected || !clipboardStyle} title="Paste the copied style onto the selected textbox">
          Paste style {clipboardStyle && <Icon name="check_circle" />}
        </button>
        <button className="btn small secondary" onClick={centerAllHorizontally} title="Horizontally center every textbox on the canvas">
          Center all
        </button>
        <span style={{ width: 1, height: 22, background: '#ddd' }} />
        {(() => {
          const activeObjs = fabricRef.current?.getActiveObjects() || [];
          const n = activeObjs.length;
          const noSel = n === 0;
          const cantDistribute = n < 3;
          const refLabel = n >= 2 ? 'selection bounding box' : 'canvas';
          const btn = (label, title, onClick, disabled) => (
            <button
              type="button"
              className="btn small secondary"
              onClick={onClick}
              disabled={disabled}
              title={title}
              style={{ padding: '4px 8px', fontSize: 0, lineHeight: 0 }}
            >
              {label}
            </button>
          );
          // Compact SVG icons matching Photoshop's alignment toolbar.
          const ICON = { width: 18, height: 18, viewBox: '0 0 18 18', fill: 'none', stroke: 'currentColor', strokeWidth: 1.4, strokeLinecap: 'round' };
          const iconAlignLeft = (
            <svg {...ICON}><line x1="2" y1="2" x2="2" y2="16" /><rect x="3" y="4" width="11" height="3" fill="currentColor" stroke="none" /><rect x="3" y="11" width="7" height="3" fill="currentColor" stroke="none" /></svg>
          );
          const iconAlignCenterH = (
            <svg {...ICON}><line x1="9" y1="2" x2="9" y2="16" /><rect x="3" y="4" width="12" height="3" fill="currentColor" stroke="none" /><rect x="5" y="11" width="8" height="3" fill="currentColor" stroke="none" /></svg>
          );
          const iconAlignRight = (
            <svg {...ICON}><line x1="16" y1="2" x2="16" y2="16" /><rect x="4" y="4" width="11" height="3" fill="currentColor" stroke="none" /><rect x="8" y="11" width="7" height="3" fill="currentColor" stroke="none" /></svg>
          );
          const iconDistributeH = (
            <svg {...ICON}><rect x="2" y="4" width="3" height="10" fill="currentColor" stroke="none" /><rect x="7.5" y="4" width="3" height="10" fill="currentColor" stroke="none" /><rect x="13" y="4" width="3" height="10" fill="currentColor" stroke="none" /></svg>
          );
          const iconAlignTop = (
            <svg {...ICON}><line x1="2" y1="2" x2="16" y2="2" /><rect x="4" y="3" width="3" height="11" fill="currentColor" stroke="none" /><rect x="11" y="3" width="3" height="7" fill="currentColor" stroke="none" /></svg>
          );
          const iconAlignCenterV = (
            <svg {...ICON}><line x1="2" y1="9" x2="16" y2="9" /><rect x="4" y="3" width="3" height="12" fill="currentColor" stroke="none" /><rect x="11" y="5" width="3" height="8" fill="currentColor" stroke="none" /></svg>
          );
          const iconAlignBottom = (
            <svg {...ICON}><line x1="2" y1="16" x2="16" y2="16" /><rect x="4" y="4" width="3" height="11" fill="currentColor" stroke="none" /><rect x="11" y="8" width="3" height="7" fill="currentColor" stroke="none" /></svg>
          );
          const iconDistributeV = (
            <svg {...ICON}><rect x="4" y="2" width="10" height="3" fill="currentColor" stroke="none" /><rect x="4" y="7.5" width="10" height="3" fill="currentColor" stroke="none" /><rect x="4" y="13" width="10" height="3" fill="currentColor" stroke="none" /></svg>
          );
          return (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px', border: '1px solid #e1e4ea', borderRadius: 8, background: '#fafbfc' }}>
              {btn(iconAlignLeft, `Align left edges (relative to ${refLabel})`, () => alignSelected('left'), noSel)}
              {btn(iconAlignCenterH, `Align horizontal centers (relative to ${refLabel})`, () => alignSelected('centerH'), noSel)}
              {btn(iconAlignRight, `Align right edges (relative to ${refLabel})`, () => alignSelected('right'), noSel)}
              {btn(iconDistributeH, 'Distribute horizontal centers evenly (needs 3+ selected)', () => distributeSelected('h'), cantDistribute)}
              <span style={{ width: 1, height: 18, background: '#ddd', margin: '0 2px' }} />
              {btn(iconAlignTop, `Align top edges (relative to ${refLabel})`, () => alignSelected('top'), noSel)}
              {btn(iconAlignCenterV, `Align vertical centers (relative to ${refLabel})`, () => alignSelected('centerV'), noSel)}
              {btn(iconAlignBottom, `Align bottom edges (relative to ${refLabel})`, () => alignSelected('bottom'), noSel)}
              {btn(iconDistributeV, 'Distribute vertical centers evenly (needs 3+ selected)', () => distributeSelected('v'), cantDistribute)}
            </div>
          );
        })()}
        <span style={{ width: 1, height: 22, background: '#ddd' }} />
        <select
          value=""
          onChange={e => { applyLayoutPreset(e.target.value); e.target.value = ''; }}
          title="Load a saved field layout onto this template"
          style={{ maxWidth: 200 }}
        >
          <option value="">Load layout preset…</option>
          {layoutPresets.map(p => (
            <option key={p._id} value={p._id}>
              {p.name} ({(p.fields || []).length} fields)
            </option>
          ))}
        </select>
        <button className="btn small secondary" onClick={saveLayoutAsPreset} title="Save current textboxes as a reusable layout preset">
          <Icon name="save" style={{ marginRight: 4 }} />Save as preset
        </button>
        <span style={{ flex: 1 }} />
        <label
          title="When on, dragging a textbox snaps to alignment with other boxes and the canvas center."
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', border: '1px solid #e1e4ea', borderRadius: 8,
            background: snapEnabled ? '#eaf6ff' : '#fafbfc',
            fontSize: 13, color: '#555', cursor: 'pointer', userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={snapEnabled}
            onChange={e => setSnapEnabled(e.target.checked)}
          />
          <Icon name="grid_guides" />Snap to align
        </label>
        {brandingImageUrl && (
          <button type="button" className="btn small secondary" onClick={selectLogoOnCanvas} title="Open logo controls in side panel">
            <Icon name="edit" style={{ marginRight: 4 }} />Edit logo
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', border: '1px solid #e1e4ea', borderRadius: 8, background: '#fafbfc' }}>
          <span style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>Watermark:</span>
          <label style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <input type="radio" name="wm-tint" checked={watermarkTint === ''} onChange={() => setWatermarkTint('')} /> none
          </label>
          <label style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <input type="radio" name="wm-tint" checked={watermarkTint === 'white'} onChange={() => setWatermarkTint('white')} /> white
          </label>
          <label style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <input type="radio" name="wm-tint" checked={watermarkTint === 'black'} onChange={() => setWatermarkTint('black')} /> black
          </label>
          <span style={{ fontSize: 13, color: '#666', marginLeft: 6 }}>Opacity:</span>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round((watermarkOpacity || 0) * 100)}
            onChange={e => setWatermarkOpacity(Number(e.target.value) / 100)}
            style={{ width: 100 }}
            title="How transparent the watermark appears on the customer preview"
          />
          <span style={{ fontSize: 12, color: '#888', minWidth: 28, textAlign: 'right' }}>{Math.round((watermarkOpacity || 0) * 100)}%</span>
        </div>
        <label className="btn small secondary" style={{ cursor: 'pointer', margin: 0 }} title="Add another overlay image on top of the template.">
          + Overlay image{overlays.length ? ` (${overlays.length})` : ''}
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => { uploadOverlay(e.target.files[0]); e.target.value = ''; }}
            disabled={overlayBusy}
          />
        </label>
        {overlays.length > 0 && (
          <button type="button" className="btn small secondary" onClick={selectOverlayOnCanvas} title="Click any overlay on the canvas to edit it; or open the most recent one here.">
            <Icon name="edit" style={{ marginRight: 4 }} />Edit overlay
          </button>
        )}
        <button className="btn small" onClick={handleGenerateAndSave} disabled={generating} style={{ background: '#29B4F1', color: '#fff' }}>
          {generating ? 'Generating…' : 'Generate Preview & Watermark'}
        </button>
        {saveAllMsg && <span style={{ fontSize: 13, color: saveAllMsg.startsWith('✓') ? '#198754' : '#dc3545' }}>{withCheck(saveAllMsg)}</span>}
        {generateMsg && <span style={{ fontSize: 13, color: generateMsg.startsWith('✓') ? '#198754' : '#dc3545' }}>{withCheck(generateMsg)}</span>}
        {presetMsg && <span style={{ fontSize: 13, color: presetMsg.startsWith('✓') ? '#198754' : '#444' }}>{withCheck(presetMsg)}</span>}
      </div>
      <div className="field-editor">
        <div className="canvas-pane" ref={stageRef} style={{ height: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f7fa', borderRadius: 8 }}>
          <canvas ref={canvasElRef} />
        </div>
        <div className="props-pane">
          {!selected && !selectedSpecial && <p style={{ color: '#888' }}>Select a textbox, logo, or overlay on the canvas to edit it. Click "+ Add Text" to create a new field.</p>}
          {selectedSpecial === 'logo' && (() => {
            const obj = getSpecialObject();
            const sp = specialScalePercent();
            const rot = obj?.angle || 0;
            const xp = obj && fabricRef.current ? (obj.left / fabricRef.current._naturalW) * 100 : 0;
            const yp = obj && fabricRef.current ? (obj.top / fabricRef.current._naturalH) * 100 : 0;
            return (
              <>
                <h3>Branding logo</h3>
                <div className="prop"><label>Size: {sp.toFixed(1)}% of template width</label>
                  <input type="range" min="2" max="80" step="0.5" value={sp} onChange={e => setSpecialScale(Number(e.target.value))} />
                </div>
                <div className="prop"><label>Rotation: {rot.toFixed(0)}°</label>
                  <input type="range" min="-180" max="180" step="1" value={rot} onChange={e => setSpecialRotation(Number(e.target.value))} />
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    {[0, -90, 90, 180].map(r => (
                      <button key={r} type="button" className="btn small secondary" onClick={() => setSpecialRotation(r)}>{r}°</button>
                    ))}
                  </div>
                </div>
                <div className="prop"><label>Horizontal: {xp.toFixed(1)}%</label>
                  <input type="range" min="0" max="100" step="0.5" value={xp} onChange={e => setSpecialPosition('x', Number(e.target.value))} />
                </div>
                <div className="prop"><label>Vertical: {yp.toFixed(1)}%</label>
                  <input type="range" min="0" max="100" step="0.5" value={yp} onChange={e => setSpecialPosition('y', Number(e.target.value))} />
                </div>
                <div className="prop"><label>Color</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button type="button" className="btn small secondary" onClick={() => setShowBrandingColor(v => !v)} style={{ background: brandingColor, minWidth: 90, color: '#000' }}>{brandingColor}</button>
                    <button type="button" className="btn small" onClick={pickBrandingColor} title="Pick a color from the template">
                      <Icon name="colorize" style={{ marginRight: 4 }} />Eyedropper
                    </button>
                  </div>
                  {showBrandingColor && <div style={{ marginTop: 8 }}><ChromePicker color={brandingColor} onChange={c => setBrandingColor(c.hex)} /></div>}
                </div>
                <p style={{ fontSize: 12, color: '#888' }}>Position, size, rotation and color are saved when you click "Save Template (all fields)".</p>
              </>
            );
          })()}
          {selectedSpecial === 'overlay' && (() => {
            const obj = getSpecialObject();
            const sp = specialScalePercent();
            const rot = obj?.angle || 0;
            const xp = obj && fabricRef.current ? (obj.left / fabricRef.current._naturalW) * 100 : 0;
            const yp = obj && fabricRef.current ? (obj.top / fabricRef.current._naturalH) * 100 : 0;
            const activeOverlay = overlays.find(o => o._overlayId === activeOverlayId);
            const overlayIdx = overlays.findIndex(o => o._overlayId === activeOverlayId);
            return (
              <>
                <h3>Overlay image{overlays.length > 1 ? ` ${overlayIdx + 1} of ${overlays.length}` : ''}</h3>
                {overlays.length > 1 && (
                  <div className="prop" style={{ background: '#f5f7fa', padding: 8, borderRadius: 6 }}>
                    <label style={{ fontSize: 12, color: '#666' }}>Switch overlay</label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {overlays.map((o, i) => (
                        <button
                          key={o._overlayId}
                          type="button"
                          className={`btn small ${activeOverlayId === o._overlayId ? '' : 'secondary'}`}
                          onClick={() => {
                            setActiveOverlayId(o._overlayId);
                            const canvas = fabricRef.current;
                            const fresh = canvas?.getObjects().find(x => x._isOverlayImage && x._overlayId === o._overlayId);
                            if (fresh) { canvas.setActiveObject(fresh); canvas.requestRenderAll(); }
                          }}
                        >#{i + 1}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="prop"><label>Size: {sp.toFixed(1)}% of template width</label>
                  <input type="range" min="2" max="200" step="0.5" value={sp} onChange={e => setSpecialScale(Number(e.target.value))} />
                </div>
                <div className="prop"><label>Rotation: {rot.toFixed(0)}°</label>
                  <input type="range" min="-180" max="180" step="1" value={rot} onChange={e => setSpecialRotation(Number(e.target.value))} />
                </div>
                <div className="prop"><label>Horizontal: {xp.toFixed(1)}%</label>
                  <input type="range" min="0" max="100" step="0.5" value={xp} onChange={e => setSpecialPosition('x', Number(e.target.value))} />
                </div>
                <div className="prop"><label>Vertical: {yp.toFixed(1)}%</label>
                  <input type="range" min="0" max="100" step="0.5" value={yp} onChange={e => setSpecialPosition('y', Number(e.target.value))} />
                </div>
                <div className="prop"><label>Tint color (optional)</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" className="btn small secondary" onClick={() => setShowOverlayColor(v => !v)} style={{ background: activeOverlay?.color || '#fff', minWidth: 90, color: '#000' }}>{activeOverlay?.color || 'none'}</button>
                    <button type="button" className="btn small" onClick={pickOverlayTint} title="Pick a color from the template">
                      <Icon name="colorize" style={{ marginRight: 4 }} />Eyedropper
                    </button>
                    {activeOverlay?.color && <button type="button" className="btn small secondary" onClick={() => setOverlayColor('')}>Clear tint</button>}
                  </div>
                  {showOverlayColor && <div style={{ marginTop: 8 }}><ChromePicker color={activeOverlay?.color || '#FFFFFF'} onChange={c => setOverlayColor(c.hex)} /></div>}
                </div>
                <button className="btn small danger" onClick={() => activeOverlayId && removeOverlayById(activeOverlayId)}>Remove this overlay</button>
                <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Saved when you click "Save Template (all fields)".</p>
              </>
            );
          })()}
          {selected && (() => {
            const activeCount = (fabricRef.current?.getActiveObjects() || []).filter(o => o && o.type === 'textbox' && !o._isBrandingLogo && !o._isOverlayImage).length;
            const multi = activeCount > 1;
            return (
            <>
              <h3>Text & Style</h3>
              {multi && (
                <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', padding: '8px 10px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
                  <strong>{activeCount} text boxes selected.</strong> Style/font/color changes apply to all of them. (Shift+click to add/remove from selection.)
                </div>
              )}
              <div className="prop"><label>Text / Placeholder (what customers see as a sample){multi ? ' — only edits the primary box' : ''}</label>
                <textarea rows={2} value={draftText} onChange={e => applyText(e.target.value)} dir={draftMeta.direction || 'rtl'} />
              </div>
              <div className="prop"><label>Font family</label>
                <div style={{ position: 'relative' }}>
                  {(() => {
                    const curName = selected.fontFamily || 'Nexa';
                    const curWeightRaw = selected.fontWeight;
                    const curWeight = curWeightRaw && curWeightRaw !== 'normal' ? String(curWeightRaw) : null;
                    return (
                      <button
                        type="button"
                        onClick={() => setFontPickerOpen(o => !o)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 12px',
                          border: '1px solid #ccc',
                          borderRadius: 4,
                          background: '#fff',
                          cursor: 'pointer',
                          fontFamily: `"${curName}"`,
                          fontWeight: curWeight || 'normal',
                          fontSize: 16,
                        }}
                      >
                        {curName} <Icon name="keyboard_arrow_down" />
                      </button>
                    );
                  })()}
                  {fontPickerOpen && (
                    <div
                      onMouseLeave={() => cancelFontPreview()}
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: 320,
                        overflowY: 'auto',
                        background: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                        zIndex: 50,
                        marginTop: 2,
                      }}
                    >
                      <div style={{ padding: 6, borderBottom: '1px solid #eee', background: '#fafafa', position: 'sticky', top: 0 }}>
                        <input
                          type="search"
                          placeholder="Search fonts…"
                          value={fontSearch}
                          onChange={e => setFontSearch(e.target.value)}
                          autoFocus
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div
                        onMouseEnter={() => previewFontHover('Nexa')}
                        onClick={() => commitFontPick('Nexa')}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 18, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 12 }}
                      >
                        <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word', fontFamily: `"Nexa"` }}>Nexa (default)</span>
                        <span style={{ flexShrink: 0, padding: '4px 10px', border: '1px solid #ddd', borderRadius: 6, background: '#fafafa', fontFamily: `"Nexa"`, fontSize: 16, lineHeight: 1.2 }} dir="rtl">אבגדהוזחטי</span>
                      </div>
                      {filteredFamilies.map(f => {
                        // Preview each row at the family's representative weight
                        // (Regular if available). Picking the row applies that
                        // same weight as a sane default; the side-panel "Weight"
                        // selector below lets the admin change it afterwards.
                        const rep = representativeWeight(f.name);
                        return (
                          <div
                            key={f._id}
                            onMouseEnter={() => previewFontHover(f.name, rep)}
                            onClick={() => commitFontPick(f.name, rep)}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 18, borderBottom: '1px solid #f8f8f8', display: 'flex', alignItems: 'center', gap: 12 }}
                          >
                            <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word', fontFamily: `"${f.name}"`, fontWeight: rep }}>
                              {f.name}
                            </span>
                            <span style={{ flexShrink: 0, padding: '4px 10px', border: '1px solid #ddd', borderRadius: 6, background: '#fafafa', fontFamily: `"${f.name}"`, fontWeight: rep, fontSize: 16, lineHeight: 1.2 }} dir="rtl">אבגדהוזחטי</span>
                          </div>
                        );
                      })}
                      {fontSearch && filteredFamilies.length === 0 && (
                        <p style={{ fontSize: 12, color: '#888', padding: 10, margin: 0 }}>No fonts match "{fontSearch}"</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {(() => {
                // Weight selector — only shown when the picked family has 2+
                // weight variants uploaded. For single-weight families (and
                // for "Nexa", which isn't in the catalog) there's nothing
                // meaningful to choose, so the row stays hidden.
                const curName = selected.fontFamily || 'Nexa';
                const ws = weightsByName[curName] || [];
                if (ws.length < 2) return null;
                const curWeightRaw = selected.fontWeight;
                const curWeight = curWeightRaw ? String(curWeightRaw) : '400';
                // If the textbox's current weight isn't actually available for
                // this family, show the closest available weight in the button
                // so the dropdown doesn't display a missing option.
                const value = ws.includes(curWeight) ? curWeight : representativeWeight(curName);
                return (
                  <div className="prop">
                    <label>Weight</label>
                    <div style={{ position: 'relative' }}>
                      <button
                        type="button"
                        onClick={() => setWeightPickerOpen(o => !o)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 12px',
                          border: '1px solid #ccc',
                          borderRadius: 4,
                          background: '#fff',
                          cursor: 'pointer',
                          fontFamily: `"${curName}"`,
                          fontWeight: value,
                          fontSize: 16,
                        }}
                      >
                        {weightLabel(value)} <Icon name="keyboard_arrow_down" />
                      </button>
                      {weightPickerOpen && (
                        <div
                          onMouseLeave={() => cancelWeightPreview()}
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: 320,
                            overflowY: 'auto',
                            background: '#fff',
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                            zIndex: 50,
                            marginTop: 2,
                          }}
                        >
                          {ws.map(w => (
                            <div
                              key={w}
                              onMouseEnter={() => previewWeightHover(w)}
                              onClick={() => commitWeightPick(w)}
                              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 18, borderBottom: '1px solid #f8f8f8', display: 'flex', alignItems: 'center', gap: 12 }}
                            >
                              <span style={{ flex: 1, minWidth: 0, fontFamily: `"${curName}"`, fontWeight: w }}>
                                {weightLabel(w)}
                              </span>
                              <span style={{ flexShrink: 0, padding: '4px 10px', border: '1px solid #ddd', borderRadius: 6, background: '#fafafa', fontFamily: `"${curName}"`, fontWeight: w, fontSize: 16, lineHeight: 1.2 }} dir="rtl">אבגדהוזחטי</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              <div className="row">
                <NumberProp label="Font size" propKey="fontSize" min={6} max={400} fallback={28} />
                <NumberProp label="Angle" propKey="angle" min={-360} max={360} fallback={0} />
              </div>
              <div className="prop"><label>Color</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button type="button" className="btn small secondary" onClick={() => setShowColor(v => !v)} style={{ background: selected.fill, color: '#fff', minWidth: 80 }}>{selected.fill}</button>
                  <button type="button" className="btn small" onClick={pickColor} title="Pick a color from the template">
                    <Icon name="colorize" style={{ marginRight: 4 }} />Eyedropper
                  </button>
                </div>
                {showColor && <div style={{ marginTop: 8 }}><ChromePicker color={selected.fill || '#000'} onChange={c => applyProp('fill', c.hex)} /></div>}
              </div>
              <div className="prop"><label>Text align</label>
                <select value={selected.textAlign || 'right'} onChange={e => applyProp('textAlign', e.target.value)}>
                  <option>left</option><option>center</option><option>right</option><option>justify</option>
                </select>
              </div>
              <div className="row">
                <NumberProp label="Line height" propKey="lineHeight" min={0.5} max={5} step={0.1} fallback={1} />
                <NumberProp label="Char spacing" propKey="charSpacing" min={-200} max={800} fallback={0} />
              </div>

              <h3 style={{ marginTop: 18 }}>Field metadata</h3>
              <div className="prop" style={{ background: '#eef6ff', padding: 10, borderRadius: 6, border: '1px solid #cfe1f5' }}>
                <label style={{ fontWeight: 600 }}>Display order in customer editor</label>
                <input
                  type="number"
                  step="1"
                  value={draftMeta.order ?? ''}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === '' || v === '-') { applyMeta('order', 0); return; }
                    const n = Number(v);
                    if (Number.isFinite(n)) applyMeta('order', n);
                  }}
                />
                <p style={{ fontSize: 12, color: '#666', margin: '4px 0 0' }}>
                  Lower numbers appear first. Field 1 is shown first, then field 2, then field 3, and so on.
                </p>
              </div>
              <div className="prop"><label>Autofill slot — what kind of field is this?</label>
                <select value={draftMeta.autofillableField || ''} onChange={e => applyMeta('autofillableField', e.target.value)}>
                  <option value="">— None —</option>
                  {autofillSlots.map(a => <option key={a._id} value={a.name}>{a.name}</option>)}
                </select>
                {autofillSlots.length === 0 && (
                  <p style={{ fontSize: 12, color: '#888', margin: '4px 0 0' }}>
                    No autofill slots defined. Add some in Admin → Autofillable Fields.
                  </p>
                )}
              </div>
              {presetInfo.kind !== 'none' && (
                <div className="prop" style={{ background: '#f0f9ff', padding: 10, borderRadius: 6, border: '1px solid #d0e9f7' }}>
                  <label style={{ fontWeight: 600, color: '#1990C7' }}>
                    Quick-fill from {presetInfo.kind === 'shul' ? 'saved shuls' : presetInfo.kind === 'hall' ? 'saved halls' : 'saved names'}
                  </label>
                  {presetInfo.items.length > 0 ? (
                    <select
                      value=""
                      onChange={e => { if (e.target.value) { applyText(e.target.value); } }}
                    >
                      <option value="">— Pick a preset to use as placeholder —</option>
                      {presetInfo.items.map((item, i) => <option key={i} value={item}>{item}</option>)}
                    </select>
                  ) : (
                    <p style={{ fontSize: 12, color: '#888', margin: '4px 0 0' }}>
                      No {presetInfo.kind} presets yet. Add some in Admin → {presetInfo.kind === 'shul' ? 'Shuls' : presetInfo.kind === 'hall' ? 'Halls' : 'People Names'}.
                    </p>
                  )}
                  {draftText && !presetInfo.items.includes(draftText) && (
                    <button
                      type="button"
                      className="btn small secondary"
                      onClick={addPresetOnTheFly}
                      style={{ marginTop: 6 }}
                      title={`Save "${draftText}" so it appears here next time`}
                    >
                      + Add "{draftText}" to {presetInfo.kind} list
                    </button>
                  )}
                </div>
              )}
              <div className="prop"><label>Direction</label>
                <select value={draftMeta.direction || 'rtl'} onChange={e => applyMeta('direction', e.target.value)}>
                  <option value="rtl">Right-to-left (Hebrew — default)</option>
                  <option value="ltr">Left-to-right</option>
                </select>
              </div>
              <div className="row">
                <NumberMeta label="Max font size" metaKey="maxFontSize" min={6} max={400} fallback={selected.fontSize || 28} />
                <NumberMeta label="Max width" metaKey="maxWidth" min={20} max={3000} fallback={selected.width || 300} />
              </div>
              <div className="prop" style={{ background: '#fff8e1', padding: 10, borderRadius: 6, border: '1px solid #ffe082' }}>
                <label style={{ fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={draftMeta.editable !== false}
                    onChange={e => applyMeta('editable', e.target.checked)}
                  /> Editable by customers
                </label>
                <p style={{ fontSize: 12, color: '#666', margin: '4px 0 0' }}>
                  When checked, customers see an input for this field. When unchecked, the text is baked into the template (decorative / static).
                </p>
              </div>
              <div className="prop" style={{ background: '#e8f5e9', padding: 10, borderRadius: 6, border: '1px solid #a5d6a7' }}>
                <label style={{ fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={!!draftMeta.prefilled}
                    onChange={e => applyMeta('prefilled', e.target.checked)}
                  /> Pre-fill input with placeholder
                </label>
                <p style={{ fontSize: 12, color: '#666', margin: '4px 0 0' }}>
                  When checked, the placeholder text appears as the actual editable value in the customer's input — it stays after they click in, and they can edit or replace it. Useful for default Hebrew text the customer usually keeps.
                </p>
              </div>
              <div className="prop"><label><input type="checkbox" checked={!!draftMeta.multiLine} onChange={e => applyMeta('multiLine', e.target.checked)} /> Multi-line</label></div>
              <div className="prop"><label><input type="checkbox" checked={!!draftMeta.isUpperCase} onChange={e => applyMeta('isUpperCase', e.target.checked)} /> All caps</label></div>
              <div className="prop"><label><input type="checkbox" checked={!!draftMeta.horizontallyCentered} onChange={e => applyMeta('horizontallyCentered', e.target.checked)} /> Horizontally centered</label></div>
            </>
            );
          })()}
        </div>
      </div>
    </>
  );
}
